import React, { useMemo } from 'react';

interface InvoiceCalculatorProps {
  bookingValue: number;
  brokeragePercent: number;
  gstPercent?: number;
  tdsPercent?: number;
  showGst?: boolean;
  showTds?: boolean;
}

/**
 * Format amount in Indian currency format (lakhs/crores)
 */
const formatIndianCurrency = (amount: number): string => {
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(amount);
};

export const InvoiceCalculator: React.FC<InvoiceCalculatorProps> = ({
  bookingValue,
  brokeragePercent,
  gstPercent = 18,
  tdsPercent = 1,
  showGst = true,
  showTds = true,
}) => {
  const calculations = useMemo(() => {
    const baseAmount = (bookingValue * brokeragePercent) / 100;
    const gstAmount = showGst ? (baseAmount * gstPercent) / 100 : 0;
    const tdsAmount = showTds ? (baseAmount * tdsPercent) / 100 : 0;
    const totalAmount = baseAmount + gstAmount - tdsAmount;

    return {
      baseAmount,
      gstAmount,
      tdsAmount,
      totalAmount,
    };
  }, [bookingValue, brokeragePercent, gstPercent, tdsPercent, showGst, showTds]);

  return (
    <div className="bg-secondary-50 rounded-xl p-4 space-y-3">
      <h4 className="text-sm font-semibold text-secondary-900 mb-4">
        Invoice Calculation Preview
      </h4>

      <div className="space-y-2">
        {/* Base Amount */}
        <div className="flex justify-between items-center text-sm">
          <span className="text-secondary-600">
            Base Amount ({brokeragePercent}% of {formatIndianCurrency(bookingValue)})
          </span>
          <span className="font-medium text-secondary-900">
            {formatIndianCurrency(calculations.baseAmount)}
          </span>
        </div>

        {/* GST */}
        {showGst && (
          <div className="flex justify-between items-center text-sm">
            <span className="text-secondary-600">
              GST @ {gstPercent}%
            </span>
            <span className="font-medium text-green-600">
              + {formatIndianCurrency(calculations.gstAmount)}
            </span>
          </div>
        )}

        {/* TDS */}
        {showTds && (
          <div className="flex justify-between items-center text-sm">
            <span className="text-secondary-600">
              TDS @ {tdsPercent}%
            </span>
            <span className="font-medium text-red-600">
              - {formatIndianCurrency(calculations.tdsAmount)}
            </span>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-secondary-200 my-2"></div>

        {/* Total */}
        <div className="flex justify-between items-center">
          <span className="text-base font-semibold text-secondary-900">
            Total Amount
          </span>
          <span className="text-lg font-bold text-primary-600">
            {formatIndianCurrency(calculations.totalAmount)}
          </span>
        </div>
      </div>

      {/* Breakdown note */}
      <p className="text-xs text-secondary-500 mt-4 pt-3 border-t border-secondary-200">
        Note: Total = Base Amount + GST - TDS (deducted at source)
      </p>
    </div>
  );
};

export default InvoiceCalculator;
